import { formatAmountWithUnit } from "@loot-os/api-contract";
import type { InventoryItem } from "@loot-os/client";
import { BoxImage } from "@loot-os/ui/components/box-image";
import { Button } from "@loot-os/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@loot-os/ui/components/dialog";
import { useState } from "react";

import { useCurrency } from "../lib/currency.js";
import { saleErrorText, sellBackCents, useSellInventoryItems } from "../lib/inventory.js";
import { useTerminology } from "../lib/terminology.js";
import { ErrorBanner } from "./ErrorBanner.js";

/**
 * Confirms selling units back (boxed's "Confirm Sale"): what goes, what it credits, and that it
 * cannot be undone. The sale is all or nothing, so a refusal names the unit that stopped it and
 * the selection is left alone for the shopper to fix.
 */
export function SellBackDialog({
  open,
  onOpenChange,
  items,
  onSold,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: InventoryItem[];
  /** Called once the sale committed, so the page can drop the selection it just sold. */
  onSold: () => void;
}) {
  const currency = useCurrency();
  const terms = useTerminology();
  const sell = useSellInventoryItems();
  const [error, setError] = useState<string | null>(null);
  const totalCents = sellBackCents(items);

  const submit = () => {
    setError(null);
    sell.mutate(
      items.map((item) => item.id),
      {
        onSuccess: () => {
          onSold();
          onOpenChange(false);
        },
        onError: (err) => setError(saleErrorText(err, terms)),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !sell.isPending && onOpenChange(false)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Sell back to the house</DialogTitle>
          <DialogDescription>
            {items.length} {items.length === 1 ? terms.item.singular : terms.item.plural} for their
            value. This can't be undone, and a sold {terms.item.singular} can no longer be shipped
            to you.
          </DialogDescription>
        </DialogHeader>

        <div className="grid max-h-64 gap-2 overflow-y-auto">
          {items.map((item) => (
            <div key={item.id} className="flex items-center gap-3 text-sm">
              <BoxImage src={item.item.imageUrl} className="size-9 rounded-md object-cover" />
              <span className="min-w-0 flex-1 truncate">{item.item.name}</span>
              <span className="font-mono text-xs">
                {formatAmountWithUnit(item.item.value, currency)}
              </span>
            </div>
          ))}
        </div>

        <div className="flex items-baseline justify-between border-t border-border pt-3 text-sm">
          <span className="text-muted-foreground">You receive</span>
          <span className="font-medium">{formatAmountWithUnit(totalCents, currency)}</span>
        </div>

        {error && <ErrorBanner>{error}</ErrorBanner>}

        <DialogFooter>
          <Button variant="ghost" disabled={sell.isPending} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={sell.isPending || items.length === 0} onClick={submit}>
            {sell.isPending
              ? "Selling…"
              : `Sell back · ${formatAmountWithUnit(totalCents, currency)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
